/**
 * ORBIT 2.0 — Date & Time Formatting Utility
 *
 * FOLLOW-UP 1 (F4): the ONE formatter for every date and time a user reads.
 * Dates  → DD-MM-YYYY
 * Times  → HH:MM (24h) in India time (Asia/Kolkata), with "IST" where there is room.
 * Stored timestamps stay UTC in the database; only the display is converted.
 * A date-only value ("2026-10-05" — invoice_date, due_date, delivery_date) is already an India
 * date and is never shifted.
 */

const IST = "Asia/Kolkata";

const istParts = (d) => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value || "";
  return { dd: get("day"), mm: get("month"), yyyy: get("year"),
           hh: get("hour") === "24" ? "00" : get("hour"), mi: get("minute") };
};

const isDateOnly = (s) => typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.trim());

/** DD-MM-YYYY. Date-only strings are shown as typed; timestamps are converted to India time. */
export function fmtDate(dateStr) {
  if (!dateStr) return "-";
  try {
    if (isDateOnly(dateStr)) {
      const [y, m, d] = dateStr.trim().split("-");
      return `${d}-${m}-${y}`;
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const p = istParts(d);
    return `${p.dd}-${p.mm}-${p.yyyy}`;
  } catch {
    return dateStr;
  }
}

/** DD-MM-YYYY HH:MM IST. Pass withSuffix=false where there is no room for the "IST" tag. */
export function fmtDateTime(dateStr, withSuffix = true) {
  if (!dateStr) return "-";
  try {
    if (isDateOnly(dateStr)) return fmtDate(dateStr);
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const p = istParts(d);
    return `${p.dd}-${p.mm}-${p.yyyy} ${p.hh}:${p.mi}${withSuffix ? " IST" : ""}`;
  } catch {
    return dateStr;
  }
}

/** HH:MM IST. */
export function fmtTime(dateStr, withSuffix = true) {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const p = istParts(d);
    return `${p.hh}:${p.mi}${withSuffix ? " IST" : ""}`;
  } catch {
    return dateStr;
  }
}

/** Short day + date, e.g. "Mon 26-10-2026" — used by a few list headers. */
export function fmtDayDate(dateStr) {
  if (!dateStr) return "-";
  try {
    const d = new Date(isDateOnly(dateStr) ? `${dateStr}T00:00:00+05:30` : dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = new Intl.DateTimeFormat("en-GB", { timeZone: IST, weekday: "short" }).format(d);
    return `${day} ${fmtDate(dateStr)}`;
  } catch {
    return dateStr;
  }
}

/** Today's date in India, as YYYY-MM-DD — for <input type="date"> values and default filters. */
export function istToday() {
  const p = istParts(new Date());
  return `${p.yyyy}-${p.mm}-${p.dd}`;
}

/** The India date N days ago, as YYYY-MM-DD. */
export function istDaysAgo(n) {
  const p = istParts(new Date(Date.now() - n * 86400000));
  return `${p.yyyy}-${p.mm}-${p.dd}`;
}

export default fmtDate;
